import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { formatDate, parseDate, toIsoDate } from '../../_lib/docx-core';

/**
 * What a date field says when there is no date: Mynte fills unknown dates with this
 * text, so a date cleared in the editor reads the same. It is document text, so it
 * stays Dutch whatever the language of the app.
 */
export const UNKNOWN_DATE = 'Onbekend';

const NUMERIC_DATE = /^\s*(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})\s*$/;
const ISO_DATE = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/;

/**
 * Date adapter for the document editor.
 *
 * Formats may be a Word date picture such as "dd/MM/yyyy" (see DateFormat): the date
 * input then shows a date exactly as the document does. Month and weekday names come
 * from the adapter's locale.
 *
 * Material's own parsing uses Date.parse, which takes "8-10-2026" as 10 August. A
 * typed date is read in the field's picture first, then as any numeric date, day
 * first (Dutch) or month first for en-US.
 */
@Injectable()
export class EditorDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: object): string {
    if (typeof displayFormat === 'string' && this.isValid(date)) {
      return formatDate(isoFromDate(date), displayFormat, String(this.locale));
    }

    return super.format(date, displayFormat);
  }

  override parse(value: unknown, parseFormat?: unknown): Date | null {
    if (typeof value === 'string') {
      if (value.trim() === '') {
        return null;
      }

      const inPicture = typeof parseFormat === 'string' ? parseDate(value, parseFormat) : undefined;
      if (inPicture) {
        return dateFromIso(inPicture);
      }

      const typed = readTyped(value, String(this.locale ?? ''));
      if (typed !== undefined) {
        return typed;
      }
    }

    return super.parse(value, parseFormat);
  }
}

/** Local-time Date for a yyyy-mm-dd value, or null when it is not a date. */
export function dateFromIso(value: unknown): Date | null {
  const iso = typeof value === 'string' ? toIsoDate(value) : undefined;
  if (!iso) {
    return null;
  }

  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];

  return new Date(year, month - 1, day);
}

/** The yyyy-mm-dd value of a picked date, in local time. */
export function isoFromDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// undefined: not a numeric date, let Material try. null: looks like a date but is not one.
function readTyped(value: string, locale: string): Date | null | undefined {
  const iso = ISO_DATE.exec(value);
  if (iso) {
    return dateFromIso(`${iso[1]}-${iso[2]}-${iso[3]}`);
  }

  const numeric = NUMERIC_DATE.exec(value);
  if (!numeric) {
    return undefined;
  }

  const monthFirst = locale.toLowerCase().startsWith('en-us');
  const [first, second, yearText] = [Number(numeric[1]), Number(numeric[2]), numeric[3]!];
  const [day, month] = monthFirst ? [second, first] : [first, second];
  // Two-digit years: 00-49 is this century, 50-99 the previous one (birth dates).
  const short = Number(yearText);
  const year = yearText.length === 2 ? (short < 50 ? 2000 + short : 1900 + short) : short;
  const pad = (n: number) => String(n).padStart(2, '0');

  return dateFromIso(`${year}-${pad(month)}-${pad(day)}`);
}
